import { RefObject, useEffect, useState } from 'react';

/**
 * Width, in px, of the grid cell a node sits in — or null outside the grid.
 *
 * The bet tray must span the FULL width of its cell (Jason 2026-08-19). It
 * cannot express that in CSS: the tray is absolutely positioned, so its
 * percentage widths resolve against the nearest positioned ancestor, which is
 * `.poker-actions` — and that chain bottoms out at TopAction's
 * `centerSectionStyle`, an absolutely positioned box with NO width, so it
 * shrinks to fit the action buttons. `width: 100%` therefore means "as wide as
 * the Fold/Call/Raise row", which is what left the tray inset from the cell
 * edges at every table count.
 *
 * The cell's own width is only ever known to the layout engine: MultiTableGrid
 * sizes cells with `flex: 1 1 0` and a `calc((100% - gaps) / N)` basis, so
 * there is no length a descendant can be handed. Deriving it from `100vw` and
 * the table count would be an approximation, and "almost reaches the edge" is
 * precisely the bug being fixed — so measure it.
 *
 * Observed rather than measured once: the cell resizes whenever the player
 * changes the table count or the window, with no remount of this subtree.
 */
export const useGridCellWidth = (
  ref: RefObject<HTMLElement | null>,
  enabled: boolean
): number | null => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setWidth(null);
      return undefined;
    }
    const cell = ref.current?.closest<HTMLElement>('[data-grid-cell]');
    if (!cell) return undefined;

    const measure = () => {
      // clientWidth excludes the cell's border but includes its padding box,
      // which is the box the felt and footer fill — the edge the tray should
      // reach.
      const next = cell.clientWidth;
      setWidth((prev) => (prev === next ? prev : next));
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(cell);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return width;
};
